import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../hooks/useTranslations';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Link2, Mail, AlertCircle, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export const ForgotPasswordPage = () => {
  const { language } = useLanguage();
  const t = useTranslations();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Page-specific texts
  const pageTexts = {
    es: {
      subtitle: 'Te enviaremos un enlace para restablecer tu contraseña',
      send: 'Enviar Enlace',
      backToLogin: 'Volver al login',
      successTitle: '¡Correo Enviado!',
      successMessage: 'Si el email existe en nuestro sistema, recibirás un enlace para restablecer tu contraseña.',
      errorMessage: 'Error al enviar el correo. Intenta de nuevo.',
    },
    en: {
      subtitle: "We'll send you a link to reset your password",
      send: 'Send Link',
      backToLogin: 'Back to login',
      successTitle: 'Email Sent!',
      successMessage: 'If the email exists in our system, you will receive a link to reset your password.',
      errorMessage: 'Error sending email. Please try again.',
    },
    de: {
      subtitle: 'Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts',
      send: 'Link senden',
      backToLogin: 'Zurück zum Login',
      successTitle: 'E-Mail gesendet!',
      successMessage: 'Wenn die E-Mail in unserem System existiert, erhalten Sie einen Link zum Zurücksetzen Ihres Passworts.',
      errorMessage: 'Fehler beim Senden der E-Mail. Bitte versuchen Sie es erneut.',
    },
    fr: {
      subtitle: 'Nous vous enverrons un lien pour réinitialiser votre mot de passe',
      send: 'Envoyer le lien',
      backToLogin: 'Retour à la connexion',
      successTitle: 'Email envoyé !',
      successMessage: 'Si l\'email existe dans notre système, vous recevrez un lien pour réinitialiser votre mot de passe.',
      errorMessage: 'Erreur lors de l\'envoi de l\'email. Veuillez réessayer.',
    },
    it: {
      subtitle: 'Ti invieremo un link per reimpostare la tua password',
      send: 'Invia link',
      backToLogin: 'Torna al login',
      successTitle: 'Email inviata!',
      successMessage: 'Se l\'email esiste nel nostro sistema, riceverai un link per reimpostare la tua password.',
      errorMessage: 'Errore nell\'invio dell\'email. Riprova.',
    },
  };

  const texts = pageTexts[language] || pageTexts.es;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError(texts.errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-subtle-gradient p-4">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center gap-2 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Link2 className="h-6 w-6" />
            </div>
            <span className="font-display text-2xl font-bold text-foreground">
              Key<span className="text-primary">Publi</span>
            </span>
          </Link>

          <Card className="shadow-xl border-border/60">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold">{texts.successTitle}</h2>
                <p className="text-muted-foreground">{texts.successMessage}</p>
                <Link to="/login">
                  <Button variant="outline" className="mt-4">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {texts.backToLogin}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-subtle-gradient p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Link2 className="h-6 w-6" />
          </div>
          <span className="font-display text-2xl font-bold text-foreground">
            Key<span className="text-primary">Publi</span> <span className="text-sm font-normal text-muted-foreground">Link Building</span>
          </span>
        </Link>

        <Card className="shadow-xl border-border/60">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl font-display">{t.auth.resetPassword}</CardTitle>
            <CardDescription>{texts.subtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t.auth.email}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-password-submit">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {texts.send}...
                  </>
                ) : (
                  texts.send
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-primary hover:underline font-medium text-sm">
                <ArrowLeft className="h-4 w-4 inline mr-1" />
                {texts.backToLogin}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
